import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Textarea,
  Table,
  Grid,
  Box,
  Text,
  Card,
  Button,
  TextInput,
  Modal,
  SegmentedControl,
  ActionIcon,
} from "@mantine/core";
import styled from "styled-components";
import * as Yup from "yup";
import { IconCopy, IconSearch } from "@tabler/icons-react";

import { BasicForm } from "@components/shared";
import { phoneValidation } from "@util/validation";

const maxMessageLength = 1000;
const recommendedMessageLength = 160;

export default function BulkCommunicationSms({
  id,
  fetchData,
  status,
  editable,
  effortId,
  templateId,
}) {
  if (!templateId) {
    return (
      <Card>
        <Text fw={600}>No template ID provided</Text>
      </Card>
    );
  }

  const [formValues, setFormValues] = useState({
    data_model: "",
  });
  const [templateInfo, setTemplateInfo] = useState(null);

  const [templateVariables, setTemplateVariables] = useState([]);
  const [templateVariableTestValues, setTemplateVariableTestValues] = useState(
    {}
  );

  useEffect(() => {
    fetchTemplateVariables();
    fetchTemplateInfo();
  }, []);

  function fetchTemplateVariables() {
    const req = {
      bulk_communicator_id: id,
      campaign_effort_id: effortId,
    };

    axios
      .post(`/bulk-communicators/${id}/template-variables/`, req)
      .then(({ data }) => {
        const templateVars = data.response.map((m) => ({
          ...m,
          formatted_name: m.name
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\s+/g, "_"),
        }));
        setTemplateVariables(templateVars);
        setTemplateVariableTestValues(
          templateVars.reduce((acc, cur) => {
            acc[cur.formatted_name] = cur.test_value;
            return acc;
          }, {})
        );
      })
      .catch((err) => {
        setTemplateVariables([]);
      });
  }

  function fetchTemplateInfo() {
    axios
      .get(`/bulk-communicators/${id}/template/`)
      .then(({ data }) => {
        const res = data.response[0];
        setTemplateInfo(res);
        if (res.data_model) {
          setFormValues({
            data_model: parseDataModel(res.data_model),
          });
        }
      })
      .catch((err) => {
        setTemplateInfo(null);
      });
  }

  const previewText = useMemo(() => {
    const text = formValues.data_model.replace(
      /\{\{(.*?)\}\}/g,
      (match, p1) => {
        return templateVariableTestValues[p1] || "VARIABLE NOT AVAILABLE";
      }
    );
    return text.slice(0, maxMessageLength);
  }, [formValues.data_model, templateVariableTestValues]);

  const getMessageLength = (message) => {
    return message.replace(/\{\{(.*?)\}\}/g, "$1").length;
  };

  if (!templateInfo) return null;

  return (
    <div>
      <Card>
        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, md: 5 }}>
            <MessageForm
              formValues={formValues}
              setFormValues={setFormValues}
              reqData={{
                campaign_effort_id: 1,
                bulk_communicator_id: id,
              }}
              getMessageLength={getMessageLength}
              templateId={templateId}
              editable={editable}
            />
            <Box
              style={{
                backgroundColor: "var(--mantine-color-gray-0)",
                borderRadius: "var(--mantine-radius-sm)",
                border: "1px solid var(--mantine-color-gray-2)",
              }}
              p="lg"
              mt="lg"
            >
              <Box>
                <Text fw={600}>Preview</Text>
                <Text mb="xs">
                  This is a preview of the message. It will not be sent to the
                  users.
                </Text>
                <StyledPreview>
                  {previewText || "No preview available"}
                </StyledPreview>
                <Box mt="lg">
                  <SendTest
                    reqData={{
                      message: previewText,
                      bulk_communicator_id: id,
                      campaign_effort_id: effortId,
                      template_id: templateId,
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 7 }}>
            <TemplateVariables
              templateVariables={templateVariables}
              templateVariableTestValues={templateVariableTestValues}
              setTemplateVariableTestValues={setTemplateVariableTestValues}
            />
          </Grid.Col>
        </Grid>
      </Card>
    </div>
  );
}

const StyledPreview = styled.p`
  background-color: #e3f2fd;
  padding: 12px 16px;
  border-radius: 18px;
  max-width: 85%;
  margin: 0;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
  word-wrap: break-word;
  font-size: 14px;
  line-height: 1.4;
  color: #000;

  &:before {
    content: "";
    position: absolute;
    left: -8px;
    top: 12px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-right: 8px solid #e3f2fd;
  }
`;

const SendTest = ({ reqData }) => {
  const [isOpen, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phoneValue, setPhoneValue] = useState("");

  function onClose() {
    setOpen(false);
    setLoading(false);
  }

  function onSubmit(formData) {
    setLoading(true);
    setPhoneValue(formData.mobile_number);
    const req = {
      ...reqData,
      ...formData,
    };

    axios
      .post(`/bulk-communicators/templates/send-test-sms/`, req)
      .then(() => {
        setLoading(false);
        toast.success("Text sent successfully");
        onClose();
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err);
      });
  }

  const fields = [
    {
      name: "mobile_number",
      label: "Mobile Number",
      mask: {
        pattern: "(999) 999-9999",
        placeholder: "(XXX) XXX-XXXX",
      },
      initialValue: phoneValue,
      required: true,
      schema: () =>
        Yup.string()
          .matches(phoneValidation, "Not a valid phone number")
          .required("Required"),
    },
  ];

  return (
    <div>
      <Button size="xs" onClick={() => setOpen(true)} radius="xl">
        Send Test Text
      </Button>
      <Modal opened={isOpen} onClose={onClose}>
        <Text size="xs" fw={600} mb="xs">
          Sending this text...
        </Text>
        <Text>{reqData.message}</Text>
        <Text size="xs" fw={600} mt="xs" mb="xs">
          to this number...
        </Text>
        <BasicForm
          fields={fields}
          loading={loading}
          onSubmit={onSubmit}
          buttonText="Send Test Text"
          buttonProps={{
            fullWidth: true,
          }}
        />
      </Modal>
    </div>
  );
};

const TemplateVariables = ({
  templateVariables,
  templateVariableTestValues,
  setTemplateVariableTestValues,
}) => {
  const [entityFilter, setEntityFilter] = useState("all");
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (searchValue) {
      setEntityFilter("all");
    }
  }, [searchValue]);

  const entityFilters = useMemo(() => {
    const filters = [{ label: "All", value: "all" }];

    const uniqueEntities = [...new Set(templateVariables.map((v) => v.entity))];

    uniqueEntities.forEach((entity) => {
      if (entity) {
        const label = entity
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" ");

        filters.push({ label: `${label} Variables`, value: entity });
      }
    });

    return filters;
  }, [templateVariables]);

  const showEntityInfo = entityFilters.length > 2;

  const filteredTemplates = templateVariables.filter((variable) =>
    entityFilter === "all"
      ? searchValue
        ? variable.name.toLowerCase().includes(searchValue.toLowerCase())
        : true
      : variable.entity === entityFilter
  );

  const allFilterSelected = entityFilter === "all";

  return (
    <Box
      style={{
        backgroundColor: "var(--mantine-color-gray-0)",
        borderRadius: "var(--mantine-radius-sm)",
        border: "1px solid var(--mantine-color-gray-2)",
      }}
      p="lg"
    >
      <Text fw={600}>Template Variables</Text>
      <Text mb="lg">
        You can use the following template variables to personalize the message.
        Use the preview to see how the message will look with the template
        variables.
      </Text>
      {showEntityInfo && (
        <SegmentedControl
          size="xs"
          value={entityFilter}
          onChange={(value) => setEntityFilter(value)}
          data={entityFilters}
          mb="xs"
        />
      )}
      <TextInput
        placeholder="Search variables"
        size="sm"
        mb="xs"
        rightSection={<IconSearch size={16} />}
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
      />
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Name</Table.Th>
            {allFilterSelected && showEntityInfo && <Table.Th>Entity</Table.Th>}
            <Table.Th>Variable Usage</Table.Th>
            <Table.Th>Test Value</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filteredTemplates.map((templateButton, index) => (
            <Table.Tr
              key={index}
              size="compact-xs"
              color="gray"
              variant="light"
            >
              <Table.Td w={150}>{templateButton.name}</Table.Td>
              {allFilterSelected && showEntityInfo && (
                <Table.Td>
                  {templateButton.entity.replaceAll("_", " ")}
                </Table.Td>
              )}
              <Table.Td>
                {templateButton.variable_usage}{" "}
                <ActionIcon
                  size="sm"
                  variant="light"
                  radius="lg"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      templateButton.variable_usage
                    );
                    toast.success("Copied!");
                  }}
                >
                  <IconCopy style={{ width: "60%", height: "60%" }} />
                </ActionIcon>
              </Table.Td>
              <Table.Td>
                <TextInput
                  value={
                    templateVariableTestValues[templateButton.formatted_name]
                  }
                  size="xs"
                  onChange={(e) =>
                    setTemplateVariableTestValues({
                      ...templateVariableTestValues,
                      [templateButton.formatted_name]: e.target.value,
                    })
                  }
                />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
};

const MessageForm = ({
  formValues,
  setFormValues,
  reqData,
  getMessageLength,
  templateId,
  editable,
}) => {
  const [saving, setSaving] = useState(false);

  function onSave() {
    if (formValues.data_model.trim() === "") {
      toast.error("Message cannot be empty");
      return;
    }

    if (!containsOnlyEnglishUnicode(formValues.data_model)) {
      toast.error(
        "Message contains non-Unicode characters. Please use only Unicode characters."
      );
      return;
    }

    const req = {
      ...reqData,
      message: sanitizeDataModel(formValues.data_model).trim(),
    };

    setSaving(true);

    axios
      .put(`/bulk-communicators/templates/${templateId}/`, req)
      .then(() => {
        setSaving(false);
        toast.success("Saved!");
      })
      .catch((err) => {
        setSaving(false);
        toast.error(err);
      });
  }

  const messageLength = getMessageLength(formValues.data_model);

  return (
    <>
      <Box mb="lg">
        <Text size="md" fw={600}>
          Message* (recommended {recommendedMessageLength} characters or less)
        </Text>
        <Text>
          This is the message that will be sent to the selected users. You may
          use template variables to personalize the message.
        </Text>
        <Text mt="sm">
          Example: "Hey {"{{"}first_name{"}}"}, we're excited to see you're in
          the {"{{"}current_tier{"}}"} tier! Your total mileage is {"{{"}
          total_mileage{"}}"}."
        </Text>
      </Box>
      <Textarea
        disabled={!editable}
        autosize
        minRows={2}
        size="lg"
        value={formValues.data_model}
        onChange={(e) =>
          e.target.value.length <= maxMessageLength
            ? setFormValues({ ...formValues, data_model: e.target.value })
            : true
        }
      />
      <Text
        size="xs"
        c={messageLength > recommendedMessageLength ? "red" : "dimmed"}
        mt="xs"
      >
        {messageLength} / {recommendedMessageLength}
      </Text>
      {editable && (
        <Button
          fullWidth
          mt="sm"
          color="green"
          size="lg"
          onClick={onSave}
          loading={saving}
        >
          Save
        </Button>
      )}
    </>
  );
};

function sanitizeDataModel(dataModel) {
  return dataModel.replaceAll('"', `'`);
}

function parseDataModel(dataModel) {
  if (JSON.stringify(dataModel) === "{}") {
    return "";
  }
  return dataModel.replaceAll(`"`, "'");
}

function containsOnlyEnglishUnicode(str) {
  const englishUnicodeRegex =
    /^[\u0000-\u007F\u0080-\u00FF\u0100-\u017F\u0180-\u024F]+$/;
  return englishUnicodeRegex.test(str);
}
